import { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import type { ChartData, ChartOptions } from 'chart.js';
import { useTranslation } from 'react-i18next';

import '../utils/chart-setup';
import { CHART_FONT_FAMILY, resolveCssColor } from '../utils/chart';
import type { DashboardChart } from '../types/dashboard.types';

type ComboData = ChartData<'bar' | 'line', number[], string>;

// PRD 01 §3.2: stacked bar per 주진단(질병군) + a "전체 환자수" line overlay on a second
// (right) Y-axis. The line is FE-derived (column sum), not a server series.
//
// SHCT-507 / ADM.029 (PRD 01 v1.6 §3.2) — the chart shows about five disease groups,
// "화면을 넘지 않는 수준". Horizontal scrolling, legend pagination and any other overflow
// treatment are explicitly out of this release, so extra groups are simply not plotted.
const MAX_DISEASE_GROUPS = 5;

export function DiseaseGroupComboChart({ chart }: { chart: DashboardChart }) {
  const { t } = useTranslation('admin');

  const data = useMemo<ComboData>(() => {
    // Deliberately the server's own order — the spec fixes a count, not a ranking,
    // so inventing one here (say, top-5 by volume) would be a rule nobody agreed to.
    const shownDatasets = chart.datasets.slice(0, MAX_DISEASE_GROUPS);

    // The line is 전체 환자수, so it stays the true total across EVERY disease group,
    // including the ones the bars leave out. Summing only the plotted five would put
    // a wrong headline number on the dashboard, which is a bigger problem than the
    // crowding this cap was raised to fix.
    const totals = chart.labels.map((_, i) =>
      chart.datasets.reduce((sum, ds) => sum + (ds.data[i] ?? 0), 0),
    );

    const bars = shownDatasets.map((ds) => ({
      type: 'bar' as const,
      label: ds.label,
      data: ds.data,
      backgroundColor: resolveCssColor(ds.color) + 'CC',
      borderColor: resolveCssColor(ds.color),
      borderWidth: 1,
      borderRadius: 4,
      stack: 'diseaseGroups',
      yAxisID: 'y',
      order: 2,
    }));

    const line = {
      type: 'line' as const,
      label: t('dashboard.totalPatientsLine'),
      data: totals,
      borderColor: '#030213',
      backgroundColor: '#03021320',
      borderWidth: 2.5,
      tension: 0.3,
      fill: false,
      pointRadius: 4,
      pointHoverRadius: 6,
      yAxisID: 'y1',
      order: 1,
    };

    return { labels: chart.labels, datasets: [...bars, line] };
  }, [chart, t]);

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { usePointStyle: true, padding: 16, font: { size: 12, family: CHART_FONT_FAMILY } },
        },
      },
      scales: {
        x: { stacked: true, grid: { display: false }, ticks: { font: { size: 11 } } },
        y: {
          stacked: true,
          beginAtZero: true,
          position: 'left',
          ticks: { font: { size: 11 } },
          title: { display: true, text: t('dashboard.axis.diseaseGroupCount'), font: { size: 11 } },
        },
        y1: {
          beginAtZero: true,
          position: 'right',
          ticks: { font: { size: 11 } },
          title: { display: true, text: t('dashboard.axis.totalCount'), font: { size: 11 } },
          grid: { drawOnChartArea: false },
        },
      },
    }),
    [t],
  );

  return <Chart type="bar" data={data as ChartData<'bar', number[], string>} options={options} />;
}
